import {
  autoPlacement,
  autoUpdate,
  offset,
  Placement,
  useFloating,
} from "@floating-ui/react-dom";
import React, { LegacyRef, MutableRefObject, useEffect, useRef } from "react";
import { useState } from "react";
import { createPortal } from "react-dom";
import ColorProvider from "./ColorProvider";
import styled, { css, keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const arrowSize = 7;
const StyledTooltip = styled.div<{
  $placement?: string;
  $width: number;
  $type?: string;
  $visible?: boolean;
}>`
  z-index: 100000;
  background: var(--blue060);
  border: 1px solid var(--blueBorder);
  color: var(--gray500);
  padding: 7px 12px;
  max-width: 350px;
  border-radius: 15px;
  font-size: 13px;
  word-break: break-word;
  pointer-events: none;
  margin-top: ${arrowSize + 1}px;
  line-height: 1.2;
  box-shadow: 0px 2px 2px rgb(0 0 0 / 0.1);
  white-space: pre-wrap;
  --arrowColor: var(--blue060);
  animation: ${fadeIn} 150ms;
  transition: opacity 150ms;
  opacity: ${(props) => (props.$visible === false ? 0 : 1)};

  * {
    line-height: 1.2;
  }

  ${(props) =>
    props.$placement?.startsWith("top") &&
    css`
      margin-top: -${arrowSize + 1}px;
    `}

  &:after {
    position: absolute;
    border-style: solid;
    content: "";
    width: 0;
    height: 0;
    left: 50%;
    margin-left: -${arrowSize}px;
    top: -${arrowSize}px;
    border-width: ${arrowSize}px;
    border-top-width: 0;
    border-color: transparent transparent var(--arrowColor) transparent;

    ${(props) =>
      props.$placement?.endsWith("start") &&
      css`
        left: ${props.$width / 2 + arrowSize + 2}px;
      `}

    ${(props) =>
      props.$placement?.endsWith("end") &&
      css`
        left: auto;
        right: ${props.$width / 2 + 2}px;
      `}

      ${(props) =>
      props.$placement?.startsWith("top") &&
      css`
        top: auto;
        bottom: -${arrowSize}px;
        border-width: ${arrowSize}px;
        border-bottom-width: 0;
        border-color: var(--arrowColor) transparent transparent transparent;
      `}
  }

  ${(props) =>
    props.$type === "danger" &&
    css`
      background: var(--red060);
      border-color: var(--redBorder);
      --arrowColor: var(--red060);
    `}

  ${(props) =>
    props.$type === "success" &&
    css`
      background: var(--green100);
      border-color: var(--greenBorder);
      --arrowColor: var(--green100);
    `}

  ${(props) =>
    props.$type === "warning" &&
    css`
      background: var(--orange100);
      border-color: var(--orangeBorder);
      --arrowColor: var(--orange100);
    `}
`;

const Tooltip = (props: {
  label: React.ReactNode;
  children: React.ReactElement & { ref?: LegacyRef<HTMLElement | null> };
  width?: number;
  placement?: "top" | "bottom";
  offset?: number;
  type?: "danger" | "warning" | "success";
  visible?: boolean;
}) => {
  const {
    children,
    label,
    placement: configPlacement,
    offset: configOffset,
    type,
    visible,
  } = props;
  const { refs, floatingStyles, placement } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: "bottom",
    middleware: [
      autoPlacement({
        allowedPlacements: [
          "bottom",
          "top",
          "bottom-start",
          "bottom-end",
          "top-start",
          "top-end",
        ].filter(
          (p) => !configPlacement || p.includes(configPlacement)
        ) as Placement[],
      }),
      offset({ mainAxis: configOffset ?? 0, alignmentAxis: -10 }),
    ],
  });
  const [hovering, setHovering] = useState(false);
  const [rendering, setRendering] = useState(false);
  const closingTimer = useRef<ReturnType<typeof setTimeout>>();

  const tooltipVisible = !!(visible || hovering);

  useEffect(() => {
    if (tooltipVisible) {
      clearTimeout(closingTimer.current);
      setRendering(true);
    } else if (rendering) {
      closingTimer.current = setTimeout(() => setRendering(false), 160);
    }
    return () => clearTimeout(closingTimer.current);
  }, [tooltipVisible, rendering]);

  const reference = refs.reference.current;
  const width = reference?.getBoundingClientRect().width ?? 30;

  useEffect(() => {
    const onScroll = () => {
      setHovering(false);
    };

    window.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
    };
  });

  return (
    <>
      {children &&
        React.cloneElement(children, {
          ref: (node: HTMLElement | null) => {
            refs.setReference(node);
            if (typeof children.ref === "function") {
              children.ref(node);
            } else if (children.ref && typeof children.ref === "object") {
              (children.ref as MutableRefObject<HTMLElement | null>).current =
                node;
            }
          },
          onMouseEnter: () => setHovering(true),
          onMouseLeave: () => setHovering(false),
        })}
      {label &&
        rendering &&
        createPortal(
          <ColorProvider>
            <StyledTooltip
              $placement={placement}
              ref={refs.setFloating}
              style={floatingStyles}
              $width={width}
              $type={type}
              $visible={tooltipVisible}
            >
              {label}
            </StyledTooltip>
          </ColorProvider>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
